import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DraggableProvided } from 'react-beautiful-dnd';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import {
	GADGET_DEFAULT_HEIGHT,
	REFRESH_LABEL_UPDATE_INTERVAL,
	GADGET_DRAG_CONTAINER_HEIGHT,
	DEPRECATED_GADGETS_MAP,
} from '@atlassian/jira-dashboard-common/src/constants.tsx';
import type { GadgetData, GadgetDisplay } from '@atlassian/jira-dashboard-common/src/types.tsx';
import { DeprecationTooltip } from '@atlassian/jira-dashboard-common/src/ui/deprecation-tooltip/index.tsx';
import { checkGadgetType } from '@atlassian/jira-dashboard-common/src/utils/check-gadget-type.tsx';
import { SkeletonGadgetContent } from '@atlassian/jira-dashboard-internal-common/src/common/gadget/gadget-skeleton/index.tsx';
import { useIsRenamable } from '@atlassian/jira-dashboard-internal-common/src/controllers/gadget-additional-state.tsx';
import {
	GadgetControllerContextConsumer,
	GadgetControllerContextProvider,
} from '@atlassian/jira-dashboard-internal-common/src/controllers/gadget/context.tsx';
import { useGadgetCommonController } from '@atlassian/jira-dashboard-internal-common/src/controllers/gadget/main.tsx';
import { useReactGadget } from '@atlassian/jira-dashboard-internal-common/src/controllers/react-gadget.tsx';
import { useLocalStorageService } from '@atlassian/jira-dashboard-internal-common/src/services/local-storage.tsx';
import { useUserPreferencesService } from '@atlassian/jira-dashboard-internal-common/src/services/user-preferences.tsx';
import { GadgetInner } from '@atlassian/jira-dashboard-internal-common/src/ui/gadget/gadget-inner/main.tsx';
import { GadgetLayoutWithAnalytics } from '@atlassian/jira-dashboard-internal-common/src/ui/gadget/main.tsx';
import { GadgetTitleWrapper } from '@atlassian/jira-dashboard-internal-common/src/ui/gadget/styled.tsx';
import { TitleReadOnly as GadgetTitleReadOnly } from '@atlassian/jira-dashboard-internal-common/src/ui/gadget/title-readonly/index.tsx';
import { GadgetToolbar } from '@atlassian/jira-dashboard-internal-common/src/ui/gadget/toolbar/index.tsx';
import { LastRefreshed } from '@atlassian/jira-dashboard-internal-common/src/ui/last-refreshed/index.tsx';
import { isConnectGadget } from '@atlassian/jira-dashboard-internal-common/src/utils/gadget-type.tsx';
import { useEcosystemRefresh } from '@atlassian/jira-dashboard-internal-common/src/utils/use-ecosystem-refresh/index.tsx';
import type { useConnectGadgetTitles } from '@atlassian/jira-dashboard-internal-common/src/utils/use-gadget-title/index.tsx';
import { decodeUserPreferences } from '@atlassian/jira-dashboard-user-preference/src/services/get-user-preferences/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useDashboardResource } from '@atlassian/jira-router-resources-dashboard/src/index.tsx';
import { useGadgetEditableController } from '../../controllers/gadget/index.tsx';
import { useDeleteGadget } from '../../services/delete-gadget.tsx';
import { useDuplicateGadget } from '../../services/duplicate-gadget/index.tsx';
import { useSaveCustomGadgetTitleById } from '../../services/save-custom-gadget-title.tsx';
import { useSaveGadgetColor } from '../../services/save-gadget-color.tsx';
import { ConfigDropdown } from './config-dropdown/index.tsx';
import messages from './messages.tsx';
import { TitleEditor } from './title-editor/index.tsx';
import { useGadgetTitleChangeErrorFlag } from './utils/index.tsx';

type GadgetEditableControllerProps = {
	data: GadgetData;
	display: GadgetDisplay;
	isConnectDisabled: boolean;
	dashboardRefreshId: number;
	children: ReactNode;
};

export const GadgetEditableController = ({
	data: {
		id,
		userPrefs,
		reactKey,
		configurable,
		refreshable: isRefreshableByJira,
		connect,
		forge,
		...data
	},
	display,
	isConnectDisabled,
	dashboardRefreshId,
	children,
}: GadgetEditableControllerProps) => {
	const isRenamable = useIsRenamable(id);
	const saveTitleById = useSaveCustomGadgetTitleById(id);
	const showSaveTitleFlag = useGadgetTitleChangeErrorFlag();
	const localStorageService = useLocalStorageService(id);
	const { update } = useDashboardResource();
	const userPreferencesService = useUserPreferencesService(id, update);

	const {
		reinitialise: onGadgetKeyChange,
		isEditable: isReactEditable,
		isLoading: isReactLoading,
	} = useReactGadget(id, reactKey);

	const automaticRefreshMs = useMemo(() => {
		const { refresh } = decodeUserPreferences(userPrefs?.fields);
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return parseInt(refresh as string, 10) * 60 * 1000 || null;
	}, [userPrefs?.fields]);

	const saveTitle = useCallback<typeof saveTitleById>(
		(...args) =>
			saveTitleById(...args).catch((error) => {
				showSaveTitleFlag();
				throw error;
			}),
		[saveTitleById, showSaveTitleFlag],
	);

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const { isInEditMode, ...editable } = useGadgetEditableController({
		id,
		userPrefs,
		configurable: isReactEditable ?? configurable,
		reactKey,
		isRenamable,
		isConnectDisabled,
		saveTitle,
		userPreferencesAction: userPrefs?.action,
		userPreferencesService,
		...data,
	});

	// Ecosystem gadgets are not supposed to re-render if they are opting out refreshing
	const ignoreGlobalDashboardRefresh = !isRefreshableByJira;

	const { onRefresh, ...common } = useGadgetCommonController({
		id,
		display,
		automaticRefreshMs: isInEditMode ? null : automaticRefreshMs, // disable auto refresh in edit mode
		localStorageService,
		dashboardRefreshId: ignoreGlobalDashboardRefresh ? null : dashboardRefreshId,
		onGadgetKeyChange,
	});

	const ecosystemRefresh = useEcosystemRefresh({ id, connect, forge });

	return (
		<GadgetControllerContextProvider
			value={{
				...common,
				...editable,
				isLoading: isReactLoading ?? isLoading,
				setIsLoading: reactKey ? () => undefined : setIsLoading,
				isInEditMode,
				isRenamable,
				onRefresh: isRefreshableByJira ? onRefresh : ecosystemRefresh.onRefresh ?? onRefresh,
			}}
		>
			<div ref={ecosystemRefresh.wrapperRef}>{children}</div>
		</GadgetControllerContextProvider>
	);
};

export type GadgetEditableProps = {
	isDraggable?: boolean;
	isDragging?: boolean;
	isConnectDisabled?: boolean;
	dashboardId: string;
	data: GadgetData;
	display: GadgetDisplay;
	dragHandleProps?: DraggableProvided['dragHandleProps'];
	refreshId: number;
	onMaximizeOrRestore: (arg1: boolean) => void;
	onMount?: (id: string, focusCallback: () => void) => void;
	onRegisterConnectGadget: ReturnType<typeof useConnectGadgetTitles>['registerConnectGadget'];
	onUnregisterConnectGadget: ReturnType<typeof useConnectGadgetTitles>['unregisterConnectGadget'];
};

export const GadgetEditable = ({
	dashboardId,
	data,
	display,
	isDraggable = false,
	isDragging = false,
	isConnectDisabled = false,
	dragHandleProps,
	refreshId,
	onMaximizeOrRestore,
	onMount,
	onRegisterConnectGadget,
	onUnregisterConnectGadget,
}: GadgetEditableProps) => {
	const { id, isMaximizable, color, forge, customisedTitle, title: defaultTitle } = data;
	const saveColor = useSaveGadgetColor(id);
	const deleteGadget = useDeleteGadget(id, customisedTitle || defaultTitle);
	const duplicateGadget = useDuplicateGadget(id);
	const gadgetHeaderRef = useRef<HTMLDivElement>(null);
	const { formatMessage } = useIntl();
	const isFirstPartyGadget = !isConnectGadget(data) && !forge;

	const isDeprecated = checkGadgetType(DEPRECATED_GADGETS_MAP, data.amdModule);

	// this onMount function is idempotent so we don't need to ensure that it is
	// called at most one time use an additional ref. The controller will already
	// handle duplicate calls to ensure that the focus callback is only called once.
	useEffect(() => onMount?.(id, () => gadgetHeaderRef.current?.focus()), [onMount, id]);

	return (
		<GadgetEditableController
			data={data}
			display={display}
			isConnectDisabled={isConnectDisabled}
			dashboardRefreshId={refreshId}
		>
			{/* TODO - these are also in context so should eventually can be wired more directly */}
			<GadgetControllerContextConsumer>
				{({
					isDisplayed,
					isMaximized,
					isMinimized,
					onMinimizeOrRestore,
					shouldUseCache,
					isLoading,
					setIsLoading,
					lastRefreshed,
					isUnconfigured,
					isEditModeEnabled,
					isEditButtonDisabled,
					isInEditMode,
					onEditModeTrigger,
					onEditModeCancel,
					title,
					isRenamable,
					isRenaming,
					setDefaultTitle,
					onRenameTrigger,
					onRenameSave,
					onRenameCancel,
				}) => (
					<GadgetLayoutWithAnalytics
						gadgetData={data}
						isDisplayed={isDisplayed}
						isContentHidden={isMinimized}
						customWrapperStyles={[isDraggable && isDragging && gadgetDraggingStyles]}
						customPlaceholder={
							isDragging && (
								<SkeletonGadgetContent
									customHeight={`${GADGET_DEFAULT_HEIGHT}px`}
									isPulsing={false}
								/>
							)
						}
						header={
							<div {...dragHandleProps}>
								<Flex alignItems="center" xcss={[isDraggable && draggableHeaderStyles]}>
									<GadgetTitleWrapper isRenaming={isRenaming} id={`gadget-title-${id}`}>
										<TitleEditor
											isRenamable={isRenamable}
											isRenaming={isRenaming}
											title={title}
											onEdit={onRenameTrigger}
											editButtonLabel={formatMessage(messages.rename, {
												gadgetName: title,
											})}
											onConfirmEdit={onRenameSave}
											onCancelEdit={onRenameCancel}
										>
											{isDeprecated && fg('endeavour_gadget_deprecation_banner_v1') ? (
												<Box xcss={titleStyles}>
													<GadgetTitleReadOnly
														title={title}
														// We don't want to render a link in edit mode
														titleUrl={undefined}
														forgeEnvironment={forge?.environment}
														forgeEnvironmentKey={forge?.environmentKey}
													/>
													<Box xcss={tooltipStyles}>
														<DeprecationTooltip />
													</Box>
												</Box>
											) : (
												<GadgetTitleReadOnly
													title={title}
													// We don't want to render a link in edit mode
													titleUrl={undefined}
													forgeEnvironment={forge?.environment}
													forgeEnvironmentKey={forge?.environmentKey}
												/>
											)}
										</TitleEditor>
									</GadgetTitleWrapper>
									<GadgetToolbar
										gadgetId={id}
										title={title}
										onMinimizeOrRestore={onMinimizeOrRestore}
										isMinimized={isMinimized}
										isMaximizable={isMaximizable}
										onMaximizeOrRestore={onMaximizeOrRestore}
										isMaximized={isMaximized}
										additionalToolbarItems={
											<ConfigDropdown
												isEditable={
													isEditModeEnabled &&
													!(isDeprecated && fg('endeavour_gadget_deprecation_remove_functionality'))
												}
												isEditDisabled={isLoading || isEditButtonDisabled}
												isDeprecatedGadget={isDeprecated}
												gadgetId={id}
												gadgetTitle={title}
												gadgetColor={color}
												onSaveColor={saveColor}
												onEditModeClick={onEditModeTrigger}
												onDeleteGadget={deleteGadget}
												onDuplicateGadget={duplicateGadget}
											/>
										}
									/>
								</Flex>
							</div>
						}
						footer={
							isFirstPartyGadget && (
								<LastRefreshed
									lastRefreshed={lastRefreshed}
									updateInterval={REFRESH_LABEL_UPDATE_INTERVAL}
								/>
							)
						}
					>
						<div data-testid="dashboard-edit.ui.gadget-editable.gadget-editable">
							<GadgetInner
								dashboardId={dashboardId}
								data={data}
								isUnconfigured={isUnconfigured}
								isEditModeEnabled={isEditModeEnabled}
								isLoading={isLoading}
								isInEditMode={isInEditMode}
								shouldUseCache={shouldUseCache}
								setIsLoading={setIsLoading}
								setDefaultTitle={setDefaultTitle}
								setTitle={onRenameSave}
								onEditModeCancel={onEditModeCancel}
								isMaximized={isMaximized}
								onRegisterConnectGadget={onRegisterConnectGadget}
								onUnregisterConnectGadget={onUnregisterConnectGadget}
							/>
						</div>
					</GadgetLayoutWithAnalytics>
				)}
			</GadgetControllerContextConsumer>
		</GadgetEditableController>
	);
};

const draggableHeaderStyles = xcss({ cursor: 'grab' });
const gadgetDraggingStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${GADGET_DRAG_CONTAINER_HEIGHT}px !important`,
});

const titleStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
});
const tooltipStyles = xcss({
	marginTop: 'space.025',
});
